import React from 'react';
import {
  GOOD_PERFORMANCE_INDICATORS,
  MEASUREMENT_CALCULATIONS,
  MEASUREMENT_LEVELS,
  PERFORMANCE_METRIC_STATUSES,
  QUALITY_DOMAINS,
  TIME_BOUNDS,
  TIME_FRAMES
} from '../../../../shared/constants/checkpoint-performance-report-metric-enums';
import { PERFORMANCE_METRIC_INPUT_TYPES } from '../PerformanceMetricInputCard';
import _ from 'lodash';
import { newLine } from '../../../../../constants/textConstants';

const getOptions = options => {
  return Object.keys(options).map(x => ({
    value: options[x].Value,
    key: options[x].Id
  }));
};

export const metricProperties = [
  {
    title: 'Status',
    field: 'status',
    description:
      'Enter the metric status for this check point collection date.' +
      newLine +
      ' - Update: Enter data for this metric in this check point.' +
      newLine +
      ' - Skip: When the metric was not measured for this check point, provide notes.' +
      newLine +
      ' - N/A: When the metric is not part of this check point date, no notes are required.' +
      newLine +
      ' - Discontinue: When the metric is no longer measured for this unit and will have no' +
      ' further updates, provide notes.',
    isEditableAfterSaving: true,
    type: PERFORMANCE_METRIC_INPUT_TYPES.multiselect,
    options: getOptions(PERFORMANCE_METRIC_STATUSES)
  },
  {
    title: 'Quality Domain',
    field: 'qualityDomain',
    description:
      'Option selected from the dropdown which best describes the metric, ' +
      'or select N/A if none apply.',
    type: PERFORMANCE_METRIC_INPUT_TYPES.multiselect,
    options: getOptions(QUALITY_DOMAINS)
  },
  {
    title: 'Quality Domain Definition',
    field: 'qualityDomainDefinition',
    description:
      'Description of how this metric provides insight into product quality ' +
      'or organizational performance.',
    type: PERFORMANCE_METRIC_INPUT_TYPES.plainText
  },
  {
    title: 'Quality Domain Definition',
    field: 'customQualityDomainDefinition',
    description:
      'Description of how this metric provides insight into product quality ' +
      'or organizational performance.',
    maxLength: 500,
    type: PERFORMANCE_METRIC_INPUT_TYPES.text
  },
  {
    title: 'Business Objective',
    field: 'businessObjective',
    description:
      'Description of the business objective most closely ' +
      'associated with the quality domain selected.',
    maxLength: 500,
    type: PERFORMANCE_METRIC_INPUT_TYPES.text
  },
  {
    title: 'Quality Objective',
    field: 'qualityObjective',
    description:
      'Description of the quality objective most closely ' +
      'associated with the quality domain selected.',
    maxLength: 500,
    type: PERFORMANCE_METRIC_INPUT_TYPES.text
  },
  {
    title: 'Metric Name',
    field: 'measurementName',
    description: 'Name of a metric used to monitor product quality or organizational performance.',
    type: PERFORMANCE_METRIC_INPUT_TYPES.text,
    maxLength: 50
  },
  {
    title: 'Level of Measure',
    field: 'measurementLevel',
    description:
      'Option selected from the dropdown which best describes ' +
      'which products are represented by the metric.',
    type: PERFORMANCE_METRIC_INPUT_TYPES.radio,
    options: getOptions(MEASUREMENT_LEVELS)
  },
  {
    title: 'Metric Description',
    description:
      'Description of how the metric is obtained (e.g., surveys, automated tools, manual' +
      ' entry), including any formulas, related metrics or sub-metrics.',
    field: 'measurementDescription',
    maxLength: 500,
    type: PERFORMANCE_METRIC_INPUT_TYPES.text
  },
  {
    title: 'Metric Calculation',
    field: 'measurementCalculation',
    description:
      'Option selected from the dropdown which best describes the metric calculation. ' +
      newLine +
      ' - Rate: comparison of two numbers with different units.' +
      newLine +
      ' - Ratio: comparison of two numbers with the same unit.',
    type: PERFORMANCE_METRIC_INPUT_TYPES.radio,
    options: getOptions(MEASUREMENT_CALCULATIONS)
  },
  {
    title: 'Unit of Measure',
    field: 'measurementUnit',
    description: (
      <span>
        The word or words describing what the metric represents, for example:{' '}
        <strong key='measurementUnitDescriptionStrongSection'>2 field actions</strong>.
      </span>
    ),
    type: PERFORMANCE_METRIC_INPUT_TYPES.text,
    maxLength: 50
  },
  {
    title: 'Numerator',
    field: 'numerator',
    description: (
      <span>
        The first word or words in a set describing what the metric represents, for example: 5{' '}
        <strong key='numeratorDescriptionStrongSection'>complaint incidents</strong> per million
        units sold.
      </span>
    ),
    type: PERFORMANCE_METRIC_INPUT_TYPES.text,
    maxLength: 500
  },
  {
    title: 'Denominator',
    field: 'denominator',
    description: (
      <span>
        The second word or words in a set describing what the metric represents, for example: 5
        complaint incidents per{' '}
        <strong key='denominatorDescriptionStrongSection'>million units sold</strong>.
      </span>
    ),
    type: PERFORMANCE_METRIC_INPUT_TYPES.text,
    maxLength: 500
  },
  {
    title: 'Time Bound',
    field: 'timeBounds',
    description:
      'Option selected from the dropdown which best describes the data that the metric represents.' +
      newLine +
      ' - All Time: # represents entire period since began measuring (best for cumulative metrics)' +
      newLine +
      ' - Period of Time: # represents a static time ' +
      '(best for metrics assigned to specific month, quarter, etc.)' +
      newLine +
      ' - Rolling Time: # represents an ongoing time ' +
      '(best for metrics assigned to the most recent # of days, months, etc.)',
    type: PERFORMANCE_METRIC_INPUT_TYPES.radio,
    options: getOptions(TIME_BOUNDS)
  },
  {
    title: 'Time Frame',
    field: 'timeFrame',
    description:
      'Option selected from the dropdown which best describes the time that the metric represents. ' +
      'This may be the same as how often the metric is captured or reported.',
    type: PERFORMANCE_METRIC_INPUT_TYPES.multiselect,
    options: getOptions(TIME_FRAMES)
  },
  {
    title: 'Blind Spots',
    field: 'limitationsOrBlindSpots',
    description:
      'Describe any limitations, blind spots, or potential for human error in collecting this metric.',
    isEditableAfterSaving: true,
    maxLength: 500,
    type: PERFORMANCE_METRIC_INPUT_TYPES.text
  },
  {
    title: 'How Metric is Used',
    field: 'measurementUse',
    description:
      'Describe how this metric helps the organization to make business decisions.' +
      ' Consider escalation pathways to a decision-maker when the metric goes out of control.',
    isEditableAfterSaving: true,
    maxLength: 1000,
    type: PERFORMANCE_METRIC_INPUT_TYPES.text
  },
  {
    title: 'Captured',
    field: 'captureFrequency',
    description:
      'Select an option from the dropdown which best describes ' +
      'how often is the metric captured internally.',
    isEditableAfterSaving: true,
    type: PERFORMANCE_METRIC_INPUT_TYPES.multiselect,
    options: getOptions(TIME_FRAMES)
  },
  {
    title: 'Reported',
    field: 'reportFrequency',
    description:
      'Select an option from the dropdown which best describes ' +
      'how often is the metric reported internally.',
    isEditableAfterSaving: true,
    type: PERFORMANCE_METRIC_INPUT_TYPES.multiselect,
    options: getOptions(TIME_FRAMES)
  },
  {
    title: 'Target Description',
    field: 'targetDescription',
    maxLength: 500,
    description: 'Description of the target objective or goal for the metric.',
    type: PERFORMANCE_METRIC_INPUT_TYPES.text
  },
  {
    title: 'Lower Limit',
    field: 'lowerLimit',
    description:
      'Enter the lower and/or upper limit as applicable.' +
      newLine +
      ' - If the metric should not drop below the target, enter a lower limit.' +
      newLine +
      ' - If the metric should not drop below one target and not go above another target, enter a unique ' +
      'lower and upper limit.' +
      newLine +
      ' - If the metric should be exactly equal to the target, enter the same lower and upper limit.',
    isEditableAfterSaving: true,
    placeholder: 'Enter Upper Limit and/or Lower Limit',
    type: PERFORMANCE_METRIC_INPUT_TYPES.decimal
  },
  {
    title: 'Upper Limit',
    field: 'upperLimit',
    description:
      'Enter the lower and/or upper limit as applicable.' +
      newLine +
      ' - If the metric should not go above the target, provide the upper limit.' +
      newLine +
      ' - If the metric should not drop below one target and not go above another target, enter a unique ' +
      'lower and upper limit.' +
      newLine +
      ' - If the metric should be exactly equal to the target, enter the same lower and upper limit.',
    isEditableAfterSaving: true,
    placeholder: 'Enter Upper Limit and/or Lower Limit',
    type: PERFORMANCE_METRIC_INPUT_TYPES.decimal
  },
  {
    title: 'Indication of Improving',
    field: 'goodPerformanceIndicator',
    description:
      'Option selected from the dropdown which best describes ' +
      'the desired long-term trend for this metric.',
    type: PERFORMANCE_METRIC_INPUT_TYPES.radio,
    options: getOptions(GOOD_PERFORMANCE_INDICATORS)
  },
  {
    title: 'Actual Value',
    field: 'actual',
    description:
      'Enter the metric value for the latest time bound and/or time frame ' +
      'as of the data collection date.',
    isEditableAfterSaving: true,
    type: PERFORMANCE_METRIC_INPUT_TYPES.decimal
  },
  {
    title: 'Notes',
    field: 'notes',
    description:
      'Describe any internal activities or external events that ' +
      'may have impacted the metric for this submission.',
    isEditableAfterSaving: true,
    maxLength: 500,
    type: PERFORMANCE_METRIC_INPUT_TYPES.text
  }
];

export const getMetricProperties = descriptions => {
  const clonedMetricProperties = _.cloneDeep(metricProperties);
  return _.map(clonedMetricProperties, item => {
    return _.extend(item, _.find(descriptions, { field: item.field }));
  });
};

export const getPerformanceReportMetricFieldsDefaultErrors = () => {
  const defaultErrorObject = { hasError: false, errorMessage: '' };

  return {
    measurementName: {
      ...defaultErrorObject,
      title: 'Metric Name'
    },
    upperLimit: {
      ...defaultErrorObject,
      title: 'Upper Limit'
    },
    lowerLimit: {
      ...defaultErrorObject,
      title: 'Lower Limit'
    },
    actual: {
      ...defaultErrorObject,
      title: 'Actual Value'
    },
    notes: {
      ...defaultErrorObject,
      title: 'Notes'
    },
    captureFrequency: {
      ...defaultErrorObject,
      title: 'Captured'
    },
    reportFrequency: {
      ...defaultErrorObject,
      title: 'Reported'
    },
    measurementDescription: {
      ...defaultErrorObject,
      title: 'Metric Description'
    },
    measurementUse: {
      ...defaultErrorObject,
      title: 'How Metric is Used'
    },
    limitationsOrBlindSpots: {
      ...defaultErrorObject,
      title: 'Blind Spots'
    },
    measurementCalculation: {
      ...defaultErrorObject,
      title: 'Metric Calculation'
    },
    measurementUnit: {
      ...defaultErrorObject,
      title: 'Unit of Measure'
    },
    numerator: {
      ...defaultErrorObject,
      title: 'Numerator'
    },
    denominator: {
      ...defaultErrorObject,
      title: 'Denominator'
    },
    targetDescription: {
      ...defaultErrorObject,
      title: 'Target Description'
    },
    goodPerformanceIndicator: {
      ...defaultErrorObject,
      title: 'Indication of Improving'
    },
    timeBounds: {
      ...defaultErrorObject,
      title: 'Time Bound'
    },
    timeFrame: {
      ...defaultErrorObject,
      title: 'Time Frame'
    },
    measurementLevel: {
      ...defaultErrorObject,
      title: 'Level of Measure'
    },
    qualityDomain: {
      ...defaultErrorObject,
      title: 'Quality Domain'
    },
    customQualityDomainDefinition: {
      ...defaultErrorObject,
      title: 'Quality Domain Definition'
    },
    businessObjective: {
      ...defaultErrorObject,
      title: 'Business Objective'
    },
    qualityObjective: {
      ...defaultErrorObject,
      title: 'Quality Objective'
    }
  };
};
